import { homesOnboardingApi } from "libs/api";
import type { ValidationResponse } from "libs/codegen/property-onboarding";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "router";
import { usePropertySelectionViewModelValue } from "../propertySelectionViewModelAtom.ts";
import { externalPropertyTypeMapper } from "../urlInput/externalPropertyTypeMapper.ts";
import { useSetProperties } from "./propertiesAtom";
import { selectablePropertiesFilter } from "./selectablePropertiesFilter";
import { useSetSelectedProperties } from "./selectedPropertiesAtom";
import { usePropertyListing } from "./usePropertyListing.ts";

export const useScrapeProperties = ({ sanitizedUrl, externalPropertyType }: ValidationResponse) => {
    const [loading, setLoading] = useState(false);

    const { shouldImportBcomWithNewFlow } = usePropertySelectionViewModelValue();

    const setProperties = useSetProperties();
    const setSelectedProperties = useSetSelectedProperties();
    const navigate = useNavigate();
    const { lang } = useParams("/:lang/list-my-property/get-started/automatic/lists");

    const { isIdle, mutate } = usePropertyListing();

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            try {
                const { properties } = await homesOnboardingApi
                    .getPropertySelection(lang, sanitizedUrl)
                    .then((o) => o.data);

                // if there are only 1 property, then call import listing and sent to hostconnect
                if (properties.length === 1 && selectablePropertiesFilter(properties[0])) {
                    const { data } = await homesOnboardingApi.importListing(lang, {
                        selected: properties.filter(selectablePropertiesFilter),
                        unselected: [],
                    });

                    window.location.assign(data.redirectTo);
                } else {
                    // force delay at least 2 seconds per design request
                    await new Promise((r) => setTimeout(r, 2000));

                    setProperties(properties);
                    setSelectedProperties(properties.filter(selectablePropertiesFilter).map((p) => p.id));
                    setLoading(false);
                }
            } catch (_) {
                navigate(
                    {
                        pathname: "/:lang/list-my-property/get-started/automatic",
                        search: "?error=importSelection",
                    },
                    {
                        params: {
                            lang,
                        },
                    },
                );
                setLoading(false);
            }
        };

        if (shouldImportBcomWithNewFlow && externalPropertyType === externalPropertyTypeMapper.Bcom)
            mutate(sanitizedUrl);
        else fetchData();
    }, [sanitizedUrl, externalPropertyType]);

    return {
        loading: loading || !isIdle,
    };
};
